import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import RoleGuard from "../components/RoleGuard";
import { isActivationChecked } from "../features/directory/activation";
import ActivationSummaryCard from "../features/directory/ActivationSummaryCard";
import { useActivationOverview } from "../features/directory/hooks";
import JobList from "../features/jobs/components/JobList";
import ManagedStoresReferenceCards from "../features/jobs/components/ManagedStoresReferenceCards";
import UploadCsvButton from "../features/jobs/components/UploadCsvButton";
import { useMyManagedStoresReference, useProvisionJobs } from "../features/jobs/hooks";
import { useAuth } from "../features/auth/useAuth";
import { apiClient } from "../lib/apiClient";
import { provisionTemplateForRole } from "../lib/provisionTemplate";
import { format, formatDistanceToNow } from "date-fns";
import type { ProvisionJobSummary } from "../features/jobs/types";
import "../App.css";

const Dashboard = () => {
  const { user } = useAuth();
  const isSuperUser = user?.role === "superuser";

  // Superusers default to the global "all jobs" view; they can switch to "mine"
  const [jobScope, setJobScope] = useState<"all" | "mine">(isSuperUser ? "all" : "mine");
  const effectiveScope = isSuperUser ? jobScope : "mine";

  const jobsQuery = useProvisionJobs(effectiveScope);
  const activationQuery = useActivationOverview({ enabled: isSuperUser });
  const activation = activationQuery.data;
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [queuedToast, setQueuedToast] = useState<string | null>(null);

  useEffect(() => {
    if (!queuedToast) return;
    const t = setTimeout(() => setQueuedToast(null), 5000);
    return () => clearTimeout(t);
  }, [queuedToast]);

  const handleUploadComplete = (job: ProvisionJobSummary) => {
    const rows = job.totalRows ?? 0;
    setQueuedToast(
      `Job "${job.name}" queued — ${rows} row${rows === 1 ? "" : "s"} processing in the background.`
    );
    jobsQuery.refetch();
  };

  const showManagedReference = Boolean(user && user.role === "retailer");
  const managedReference = useMyManagedStoresReference(showManagedReference);

  const jobs = jobsQuery.data ?? [];

  const kpi = useMemo(() => {
    const total = jobs.length;
    const completedJobs = jobs.filter((j) => j.status === "completed").length;
    const needsReview = jobs.filter((j) => j.status === "failed" || j.status === "in_progress").length;
    const sorted = [...jobs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const latest = sorted[0];
    const completedPct = total ? Math.round((completedJobs / total) * 100) : 0;
    const reviewPct = total ? Math.round((needsReview / total) * 100) : 0;
    return {
      total,
      completedJobs,
      needsReview,
      latest,
      completedPct,
      reviewPct
    };
  }, [jobs]);

  const downloadTemplate = async () => {
    setTemplateError(null);
    try {
      const { apiPath, filename } = provisionTemplateForRole(user?.role);
      const res = await apiClient.get<Blob>(apiPath, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(res.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setTemplateError("Unable to download the template. Confirm you’re signed in and try again.");
    }
  };

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero-text">
          <h1>Provisioning jobs</h1>
          <p>
            Upload approved CSVs to run provisioning batches. Use the catalog below for valid store IDs and role
            labels, then track outcomes on each job card.
          </p>
        </div>
        <div className="dashboard-actions">
          <button
            type="button"
            className="dashboard-btn-outline"
            onClick={() => jobsQuery.refetch()}
            disabled={jobsQuery.isFetching}
          >
            {jobsQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <RoleGuard allow={["superuser", "retailer"]}>
            <button type="button" className="dashboard-btn-outline" onClick={() => void downloadTemplate()}>
              Download template
            </button>
            <UploadCsvButton onUploaded={handleUploadComplete} />
          </RoleGuard>
        </div>
      </section>
      {templateError ? (
        <div style={{ color: "#b91c1c", fontSize: "0.9rem" }}>{templateError}</div>
      ) : null}
      {queuedToast ? (
        <div
          role="status"
          style={{
            padding: "0.75rem 1rem",
            borderRadius: "0.75rem",
            border: "1px solid #86efac",
            backgroundColor: "#ecfdf5",
            color: "#166534",
            fontSize: "0.9rem",
            fontWeight: 600
          }}
        >
          {queuedToast}
        </div>
      ) : null}

      {isSuperUser ? (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem" }}>
          <span style={{ color: "#64748b", fontWeight: 500 }}>Viewing:</span>
          <div
            role="group"
            aria-label="Job scope"
            style={{ display: "flex", borderRadius: "0.5rem", overflow: "hidden", border: "1px solid #e2e8f0" }}
          >
            <button
              type="button"
              onClick={() => setJobScope("all")}
              style={{
                padding: "0.35rem 0.85rem",
                border: "none",
                cursor: "pointer",
                fontWeight: effectiveScope === "all" ? 700 : 400,
                background: effectiveScope === "all" ? "#2563eb" : "#f8fafc",
                color: effectiveScope === "all" ? "#fff" : "#475569",
                transition: "background 0.15s"
              }}
            >
              All jobs
            </button>
            <button
              type="button"
              onClick={() => setJobScope("mine")}
              style={{
                padding: "0.35rem 0.85rem",
                border: "none",
                borderLeft: "1px solid #e2e8f0",
                cursor: "pointer",
                fontWeight: effectiveScope === "mine" ? 700 : 400,
                background: effectiveScope === "mine" ? "#2563eb" : "#f8fafc",
                color: effectiveScope === "mine" ? "#fff" : "#475569",
                transition: "background 0.15s"
              }}
            >
              My jobs
            </button>
          </div>
          {effectiveScope === "all" ? (
            <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>
              CSV upload jobs from all location managers (self-registration requests are under Review requests)
            </span>
          ) : null}
        </div>
      ) : null}

      <section className="dashboard-kpi-row" aria-label="Job summary">
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-icon" style={{ background: "#eff6ff", color: "#2563eb" }} aria-hidden>
            📄
          </div>
          <div style={{ minWidth: 0 }}>
            <p className="dashboard-kpi-label">Total jobs</p>
            <p className="dashboard-kpi-value">{kpi.total}</p>
            <p className="dashboard-kpi-sub">
            {isSuperUser && effectiveScope === "all" ? "All users, all time" : "All time"}
          </p>
          </div>
        </div>
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-icon" style={{ background: "#ecfdf5", color: "#16a34a" }} aria-hidden>
            ✓
          </div>
          <div style={{ minWidth: 0 }}>
            <p className="dashboard-kpi-label">Completed</p>
            <p className="dashboard-kpi-value">{kpi.completedJobs}</p>
            <p className="dashboard-kpi-sub">{kpi.total ? `${kpi.completedPct}% of all jobs` : "—"}</p>
          </div>
        </div>
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-icon" style={{ background: "#fff7ed", color: "#ea580c" }} aria-hidden>
            !
          </div>
          <div style={{ minWidth: 0 }}>
            <p className="dashboard-kpi-label">Needs review</p>
            <p className="dashboard-kpi-value">{kpi.needsReview}</p>
            <p className="dashboard-kpi-sub">{kpi.total ? `${kpi.reviewPct}% of all jobs` : "Failed or in progress"}</p>
          </div>
        </div>
        <div className="dashboard-kpi-card">
          <div className="dashboard-kpi-icon" style={{ background: "#f5f3ff", color: "#7c3aed" }} aria-hidden>
            🕐
          </div>
          <div style={{ minWidth: 0 }}>
            <p className="dashboard-kpi-label">Last upload</p>
            <p className="dashboard-kpi-value" style={{ fontSize: "1.05rem", fontWeight: 700 }}>
              {kpi.latest
                ? formatDistanceToNow(new Date(kpi.latest.createdAt), { addSuffix: true })
                : "—"}
            </p>
            <p className="dashboard-kpi-sub">
              {kpi.latest
                ? format(new Date(kpi.latest.createdAt), "MMM d, yyyy 'at' h:mm a")
                : "No uploads yet"}
            </p>
          </div>
        </div>
      </section>

      {isSuperUser && activation && isActivationChecked(activation.activation_sync) ? (
        <ActivationSummaryCard
          counts={activation.totals}
          finishedAt={activation.activation_sync.finished_at}
          title="Workvivo logins"
          action={
            <Link to="/activation" className="directory-action-link">
              See logins by location <span aria-hidden>→</span>
            </Link>
          }
        />
      ) : null}

      {showManagedReference ? (
        <ManagedStoresReferenceCards reference={managedReference} onDownloadTemplate={() => void downloadTemplate()} />
      ) : null}

      <section aria-labelledby="jobs-heading">
        <h2 id="jobs-heading" className="visually-hidden">
          Job list
        </h2>
        {jobsQuery.isLoading ? (
          <div style={{ color: "#94a3b8" }}>Loading jobs…</div>
        ) : jobsQuery.isError ? (
          <div style={{ color: "#b91c1c" }}>Failed to load provisioning jobs.</div>
        ) : (
          <JobList jobs={jobs} />
        )}
      </section>
    </div>
  );
};

export default Dashboard;
